// @wc-ignore-file

import { useMatch, useParams } from "@tanstack/react-router";

import { useWorkspacesQuery } from "@/features/containers";
import {
  type SettingsContextSearch,
  validateSettingsContextSearch,
} from "@/features/profiles/settings-context-search";

export interface HeaderContext {
  /** Effective workspace: the route param, else the Settings search context. */
  slug: string | undefined;
  /** Effective project, same precedence as `slug`. */
  projectSlug: string | undefined;
  /** The project route param only: the sidebar exists on project pages alone. */
  routeProjectSlug: string | undefined;
  /**
   * CR-226: name of the $slug route's workspace (route context). Undefined
   * off workspace routes (incl. Settings) or when it has none — the workspace
   * combobox then falls back to the slug.
   */
  workspaceName: string | undefined;
  /** CR-189: the id of the project page's project (route context), for the Help link. */
  routeProjectId: string | undefined;
  isIncognito: boolean;
  /**
   * CR-183: the caller is a contributor here — on the whole workspace, or on
   * the current project. The header then drops the workspace search (and the
   * sidebar trigger: a contributor's project page has no sidebar).
   */
  isContributor: boolean;
  /** True on Settings when the context came from its search params. */
  isSettingsContext: boolean;
  /** Search for the profile-menu Settings link (undefined: bare /account). */
  settingsLinkSearch: SettingsContextSearch | undefined;
}

/**
 * CR-144: the header's workspace/project context. Route params win; on
 * Settings (/account, which has no params) the context rides in search params.
 * Those are URL input, so they are re-validated here (the router merges the raw
 * search under the validated one) and only count once the workspace is one of
 * the user's own workspaces.
 */
export function useHeaderContext(userId: string, logoOnly: boolean): HeaderContext {
  const { slug: routeSlug, projectSlug: routeProjectSlug } = useParams({ strict: false });
  // CR-116: in an incognito (superuser read-only) workspace the member-scoped
  // header widgets would only fire failing RLS reads, and a flag-less Home link
  // would take the member load path and clear the active workspace.
  const workspaceContext = useMatch({
    from: "/_authenticated/workspaces/$slug",
    shouldThrow: false,
  })?.context;
  const workspaceMode = workspaceContext?.workspaceAccess.mode;
  // CR-226: the workspace the $slug route already resolved, so the header can
  // label it without waiting for (or depending on) the member workspace list.
  const workspaceName = workspaceContext?.workspace?.name || undefined;
  const isIncognito = workspaceMode === "incognito";
  const isWorkspaceContributor = workspaceMode === "contributor";
  const projectContext = useMatch({
    from: "/_authenticated/workspaces/$slug/projects/$projectSlug",
    shouldThrow: false,
  })?.context;
  const isProjectContributor = projectContext?.projectAccess === "contributor";
  const routeProjectId = projectContext?.project?.id;
  const isContributor = isWorkspaceContributor || isProjectContributor;
  const accountSearch = useMatch({ from: "/_authenticated/account", shouldThrow: false })?.search;
  const settingsSearch =
    routeSlug || !accountSearch ? {} : validateSettingsContextSearch(accountSearch);
  const { data: memberWorkspaces } = useWorkspacesQuery(
    settingsSearch.workspaceSlug && !logoOnly ? userId : undefined,
  );
  const isSettingsContext =
    !!settingsSearch.workspaceSlug &&
    !isIncognito &&
    !!memberWorkspaces?.some((workspace) => workspace.slug === settingsSearch.workspaceSlug);

  const slug = isSettingsContext ? settingsSearch.workspaceSlug : routeSlug;
  const projectSlug = isSettingsContext ? settingsSearch.projectSlug : routeProjectSlug;
  const settingsLinkSearch =
    slug && !isIncognito
      ? { workspaceSlug: slug, ...(projectSlug ? { projectSlug } : {}) }
      : undefined;

  return {
    slug,
    projectSlug,
    routeProjectSlug,
    workspaceName,
    routeProjectId,
    isIncognito,
    isContributor,
    isSettingsContext,
    settingsLinkSearch,
  };
}
